// The signed-in player, from the server (/api/player/*, server/players.ts): asked once when the site opens, then kept
// here; every page reads it with useAccount(). Signing in also writes the identity the game reads (identity.ts).
import { useEffect, useState } from 'react';
import type { PlayerInfo } from '../../shared/account';
import { clearIdentity, saveIdentity } from './identity';

/** undefined: not known yet (the first answer is on its way); null: signed out */
let current: PlayerInfo | null | undefined;
const listeners = new Set<() => void>();

function set(p: PlayerInfo | null) {
  current = p;
  if (p) saveIdentity({ displayName: p.displayName, instagram: p.instagram, guest: false });
  else clearIdentity();
  for (const fn of listeners) fn();
}

let asking: Promise<void> | null = null;
export function refreshAccount() {
  asking ??= fetch('/api/player/me', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : { player: null }))
    .then((j: { player: PlayerInfo | null }) => set(j.player))
    .catch(() => set(null))
    .finally(() => (asking = null));
  return asking;
}

export function useAccount(): PlayerInfo | null | undefined {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    if (current === undefined) void refreshAccount();
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return current;
}

async function post(path: string, body: unknown): Promise<{ player?: PlayerInfo; error?: string }> {
  try {
    const r = await fetch(`/api/player/${path}`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = (await r.json().catch(() => ({}))) as { player?: PlayerInfo; error?: string };
    if (!r.ok) return { error: j.error ?? 'Something went wrong — try again.' };
    if (j.player) set(j.player);
    return j;
  } catch {
    return { error: 'Can’t reach the server — check your connection and try again.' };
  }
}

export const signUp = (b: { email: string; password: string; displayName: string; instagram: string }) => post('signup', b);
export const logIn = (b: { email: string; password: string }) => post('login', b);
export const forgotPassword = (email: string) => post('forgot', { email });
export const resetPassword = (token: string, password: string) => post('reset', { token, password });
export async function logOut() {
  await post('logout', {});
  set(null);
}
